import {
  ActionButton,
  formatDate,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Padding,
  TextEllipsis,
  useOutputDescriptionProcessingStatesQuery,
  useSystemClassifierItemsBy,
  Form,
  CalculateOutputDataSchema,
  FormField,
  FormSelect,
  useSystemClassifierSelectOptions,
  CalculateOutputDataType,
  OutputDescriptionProcessingStatesType,
  SystemClassifierItemDto,
  GetApiErrorMessage,
  useCalculateOutputData,
  FormAlerts,
  useMutationErrors,
  useAlertContext,
  Detail,
  createApiErrorTranslator,
} from '@atlas-frontend/sdk';
import { Flex } from 'antd';
import { FC } from 'react';
import translations from './OutputDataActionModal.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { ActiveOutputsTable } from './ActiveOutputsTable';
import { useFormContext, useWatch } from 'react-hook-form';
import { OutputDataActionParams } from '../OutputDataDirectory';

const { addTranslations, t, exists } = i18nNsFactory(
  'output-data-action-modal',
);
addTranslations(translations);

const translateMutationError: GetApiErrorMessage = createApiErrorTranslator({
  t,
  exists,
});

export interface CalculationModalProps {
  params: OutputDataActionParams;
  onClose: () => void;
  validate?: boolean;
}

export const CalculationModal: FC<CalculationModalProps> = (props) => {
  const { params, onClose, validate = false } = props;
  const { selectAll, hasAccess, ...otherParams } = params;
  const { data, isLoading } =
    useOutputDescriptionProcessingStatesQuery(otherParams);
  const { mutateAsync } = useCalculateOutputData();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);
  const { resetAlert, setSuccess } = useAlertContext();
  const systemClassifierItemsById = useSystemClassifierItemsBy(
    ['SOURCE_DATA_VERSION', 'OUTPUT_TYPE'],
    'code',
  );

  if (!data) {
    return null;
  }

  return (
    <Form
      schema={CalculateOutputDataSchema}
      defaultValues={getDefaultValues(
        data,
        systemClassifierItemsById['SOURCE_DATA_VERSION'],
        systemClassifierItemsById['OUTPUT_TYPE'],
        selectAll,
        validate,
      )}
      onSubmit={(data: CalculateOutputDataType) => {
        resetAlert();
        clearMutationErrors();
        return mutateAsync({ data })
          .then(() => {
            onClose();
            setSuccess(t('alert.success.calculated'));
          })
          .catch((error) => {
            setMutationErrors(error?.response?.data?.errors ?? []);
          });
      }}
      serverErrors={mutationErrors}
    >
      <CalculationModalInner
        data={data}
        isLoading={isLoading}
        onClose={onClose}
        validate={validate}
        hasGroupAccess={hasAccess}
      />
    </Form>
  );
};

const CalculationModalInner: FC<{
  data: OutputDescriptionProcessingStatesType;
  isLoading?: boolean;
  onClose: () => void;
  validate: boolean;
  hasGroupAccess: boolean;
}> = (props) => {
  const { data, isLoading, onClose, validate, hasGroupAccess } = props;

  const { setValue } = useFormContext<CalculateOutputDataType>();
  const selectedOutputIds = useWatch<CalculateOutputDataType>({
    name: 'outputIds',
  }) as number[];

  const systemClassifierItemsById = useSystemClassifierItemsBy(['FREQUENCY']);
  const sourceDataVersionOptions = useSystemClassifierSelectOptions([
    'SOURCE_DATA_VERSION',
  ])['SOURCE_DATA_VERSION'];

  if (!data) return null;

  return (
    <Modal
      onCancel={onClose}
      width="60rem"
      loading={isLoading}
      footer={
        <ModalFooter>
          <Flex gap="small" justify="end">
            <ActionButton type="abort" onClick={onClose} />
            <ActionButton
              type="formSubmit"
              label={t(
                `button.${validate ? 'calculateAndValidate' : 'calculate'}`,
              )}
              disabled={selectedOutputIds.length === 0}
            />
          </Flex>
        </ModalFooter>
      }
    >
      <ModalHeader>
        <ModalTitle>
          <TextEllipsis>
            {t('title', { count: data.outputs.length })}
          </TextEllipsis>
        </ModalTitle>
      </ModalHeader>
      <FormAlerts />
      <Padding scrollable>
        <Flex vertical gap="small">
          <Detail label={t('group')}>
            {`${data?.groupCode} - ${data.groupName}`}
          </Detail>
          <Detail label={t('valueDate')}>{formatDate(data.valueDate)}</Detail>
          <Detail label={t('frequency')}>
            {systemClassifierItemsById['FREQUENCY'].get(data.frequencyId)?.name}
          </Detail>
        </Flex>
        <Padding top>
          <FormField
            name="sourceDataVersionId"
            label={t('field.versionStatusId.label')}
            style={{ maxWidth: '20rem' }}
            required
          >
            <FormSelect
              name="sourceDataVersionId"
              options={sourceDataVersionOptions}
            />
          </FormField>
        </Padding>
        <Padding top>
          <ActiveOutputsTable
            mode="edit"
            data={data}
            hasGroupAccess={hasGroupAccess}
            selectionMode="multiple"
            selectedOutputIds={selectedOutputIds}
            onSelectedOutputIdsChange={(selectedIds) =>
              setValue('outputIds', selectedIds)
            }
          />
        </Padding>
      </Padding>
    </Modal>
  );
};

const getDefaultValues = (
  data: OutputDescriptionProcessingStatesType,
  sourceDataVersionMap: Map<string, SystemClassifierItemDto>,
  outputTypeMap: Map<string, SystemClassifierItemDto>,
  selectAll = true,
  validate = false,
) => {
  const reportId = outputTypeMap.get('REPORT')?.id;
  const publishingId = sourceDataVersionMap.get('PUBLISHING')?.id;
  const latestId = sourceDataVersionMap.get('LATEST')?.id;
  const onlyReports = data?.outputs.every(
    (output) => output.outputTypeId === reportId,
  );

  return {
    groupId: data.groupId,
    valueDate: data.valueDate,
    sourceDataVersionId: onlyReports ? publishingId : latestId,
    outputIds: !selectAll
      ? []
      : [...(data?.outputs.map((output) => output.id) || [])],
    validate,
  };
};
